import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, View, RefreshControl, ScrollView } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useMutation } from '@apollo/client';

import { useBooleanState } from '../../../common/utils/hooks';
import { useUserContext } from '../../../common/utils/context';
import { formatDate } from '../../../common/utils/utils';
import { DetailContainer } from '../../containers';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useOfflineQueue } from '../../offline';
import { Dialog, WarningCallout } from '../../components';
import EmptyState from '../../design/components/EmptyState';
import PageHeading from '../../design/components/PageHeading';
import { useTheme } from '../../theme';

import PickListSummaryGrid from './PickListSummaryGrid';
import DocketSelector from './DocketSelector';
import PickListSummarySettings from './PickListSummarySettings';
import PickListSummaryList from './PickListSummaryList';
import PickListItemEdit from './PickListItemEdit';
import PickListSummaryExpiryWarningsDialog from './PickListSummaryExpiryWarningsDialog';
import { MODIFY_PICKLIST_ITEMS_QUANTITY } from '../../../common/graphql/pickListItem';
import { ROLE_PERMISSIONS } from '../../../common/constants';

const MODES = {
	LIST: 'list',
	GRID: 'grid',
};

const MODE_OPTIONS = [
	{ mode: MODES.LIST, icon: 'list', label: 'List view' },
	{ mode: MODES.GRID, icon: 'grid', label: 'Grid view' },
];

const PickListSummary = ({
	refetch,
	data: serviceRun,
	subtitle,
}) => {
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);

	const { user, hasPermission, tenant, tenantSettings } = useUserContext();
	const { isOnline } = useOfflineQueue();
	const canEditPickList = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_PICK_LISTS);
	const canPick = canEditPickList && isOnline;

	const { settings: {
		pickListSummaryHideImages,
		pickListSummaryShowProductName,
		pickListSummarySort,
		pickListSummaryMode,
		pickListSummarySelectAllMachines,
		pickListSummaryShowAllSelections,
		pickListSummaryShowExpiryWarnings,
	} } = user;

	const [hideImages, toggleHideImages] = useBooleanState(pickListSummaryHideImages);
	const [showAllSelections, toggleShowAllSelections] = useBooleanState(pickListSummaryShowAllSelections);
	const [showProductNames, toggleShowProductNames] = useBooleanState(pickListSummaryShowProductName);
	const [showExpiryWarnings, toggleShowExpiryWarnings] = useBooleanState(pickListSummaryShowExpiryWarnings);
	const [sortMethod, setSortMethod] = useState(pickListSummarySort || 'quantityDsc');
	const [mode, setMode] = useState(pickListSummaryMode || MODES.LIST);

	// When enabled, show expiry warning indicators in pick list summary.
	// This is kept separate from the pick list detail expiry display settings.
	const expiryWarningsConfig = useMemo(() => ({
		enabled: Boolean(showExpiryWarnings),
		tenantSettings,
		tenantTimezone: tenant?.timezone,
		serviceRunPlanned: serviceRun?.planned,
	}), [showExpiryWarnings, tenantSettings, tenant?.timezone, serviceRun?.planned]);

	// used in grid only
	const [selectedDocketIds, setSelectedDocketIds] =
		useState(pickListSummarySelectAllMachines ? serviceRun.serviceDockets.filter(docket => docket.pickList).map(docket => docket.id) : []);
	const Container = mode === MODES.LIST ? ScrollView : View;
	const mappedPickListItems = serviceRun.serviceDockets.filter(docket => docket.pickList).map(d => d.pickList.pickListItems).flat();
	const [selectedItemsIds, setSelectedItemsIds] = useState([]);
	const clearEditedSelection = useCallback(() => setSelectedItemsIds([]), [setSelectedItemsIds]);
	const [modifyPicklistItemsQuantityMutation] = useMutation(MODIFY_PICKLIST_ITEMS_QUANTITY);

	const setPickListItemQuantity = useCallback((ids, quantity) => {
		return modifyPicklistItemsQuantityMutation({ variables: { ids, quantity } });
	}, [modifyPicklistItemsQuantityMutation]);

	const themedStyles = useThemedStyles(createThemedStyles);
	const { theme: { ui: { colors } } } = useTheme();

	const [expiryWarningsDialogItem, setExpiryWarningsDialogItem] = useState(null);

	const openExpiryWarningsDialog = useCallback((summaryItem) => {
		if (!summaryItem?.expiryWarnings || summaryItem.expiryWarnings.status === 'none') {
			return;
		}
		setExpiryWarningsDialogItem(summaryItem);
	}, []);

	const closeExpiryWarningsDialog = useCallback(() => {
		setExpiryWarningsDialogItem(null);
	}, []);

	// Route name and date sit on their own lines so a long route name can wrap
	// without ever breaking the date mid-value.
	const summarySubtitle = subtitle || [
		serviceRun.route?.name || 'Service run',
		serviceRun.planned ? formatDate(serviceRun.planned, 'DAY_MONTHWORD_YEAR') : null,
	].filter(Boolean).join('\n');

	return (
		<Container
			style={themedStyles.container}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
			keyboardShouldPersistTaps="handled"
			{...(mode === MODES.LIST ? { contentContainerStyle: themedStyles.listContent } : {})}
		>
			<PageHeading
				title="Pick list summary"
				subtitle={summarySubtitle}
				style={themedStyles.headerContainer}
				action={(
					<View style={themedStyles.modeContainer}>
						{MODE_OPTIONS.map(option => {
							const isSelected = mode === option.mode;
							return (
								<Pressable
									key={option.mode}
									onPress={() => setMode(option.mode)}
									accessibilityRole="button"
									accessibilityLabel={option.label}
									accessibilityState={{ selected: isSelected }}
									style={({ pressed }) => [
										themedStyles.modeButton,
										isSelected && themedStyles.modeButtonSelected,
										pressed && themedStyles.pressed,
									]}
								>
									<Feather name={option.icon} size={22} color={isSelected ? colors.accent : colors.iconMuted} />
								</Pressable>
							);
						})}
					</View>
				)}
			/>
			<View style={themedStyles.optionsContainer}>
				<PickListSummarySettings
					showAllSelections={showAllSelections}
					hideImages={hideImages}
					showProductNames={showProductNames}
					showExpiryWarnings={showExpiryWarnings}
					sortMethod={sortMethod}
					toggleShowAllSelections={toggleShowAllSelections}
					toggleHideImages={toggleHideImages}
					toggleShowProductNames={toggleShowProductNames}
					toggleShowExpiryWarnings={toggleShowExpiryWarnings}
					setSortMethod={setSortMethod}
				/>
				{
					mode === MODES.GRID && (
						<DocketSelector
							serviceDockets={serviceRun.serviceDockets}
							selectedDocketIds={selectedDocketIds}
							setSelectedDocketIds={setSelectedDocketIds}
						/>
					)
				}
			</View>
			{
				(!canEditPickList || !isOnline) && (
					<View style={themedStyles.callouts}>
						{
							!canEditPickList && (
								<WarningCallout text="You have read-only access. Pick actions require permission to edit pick lists." />
							)
						}
						{
							!isOnline && (
								<WarningCallout text="Connect to the internet before changing pick quantities or picked status." />
							)
						}
					</View>
				)
			}
			{ mode === MODES.LIST && (
				<PickListSummaryList
					onClickQuantity={canPick ? setSelectedItemsIds : undefined}
					canPick={canPick}
					showAllSelections={showAllSelections}
					serviceRun={serviceRun}
					hideImages={hideImages}
					sortMethod={sortMethod}
					showExpiryWarnings={showExpiryWarnings}
					expiryWarningsConfig={expiryWarningsConfig}
					onOpenExpiryWarningsDialog={openExpiryWarningsDialog}
				/>
			)}
			{ mode === MODES.GRID && !selectedDocketIds.length && (
				<EmptyState icon="grid" title="No POS selected" message="Select POS to show the grid." />
			)}
			{ mode === MODES.GRID && selectedDocketIds.length > 0 && (
				<PickListSummaryGrid
					showAllSelections={showAllSelections}
					onClickQuantity={canPick ? setSelectedItemsIds : undefined}
					canPick={canPick}
					selectedDocketIds={selectedDocketIds}
					showProductNames={showProductNames}
					sortMethod={sortMethod}
					serviceRun={serviceRun}
					showExpiryWarnings={showExpiryWarnings}
					expiryWarningsConfig={expiryWarningsConfig}
					onOpenExpiryWarningsDialog={openExpiryWarningsDialog}
				/>
			)}
			<Dialog
				isOpen={selectedItemsIds?.length > 0}
				onClose={clearEditedSelection}
			>
				<PickListItemEdit
					pickListItems={mappedPickListItems.filter(i => selectedItemsIds?.includes(i.id))}
					onButtonPress={setPickListItemQuantity}
					quantityFieldName="quantity"
					extraQuantityFieldName="extraQuantity"
				/>
			</Dialog>
			<PickListSummaryExpiryWarningsDialog
				item={expiryWarningsDialogItem}
				onClose={closeExpiryWarningsDialog}
				serviceDockets={serviceRun.serviceDockets}
				planned={serviceRun?.planned}
			/>
		</Container>
	);
};

const createThemedStyles = ({ ui: { colors, radius, touch, space } }) => ({
	container: {
		flex: 1,
	},
	listContent: {
		paddingBottom: space.xl,
	},
	pressed: {
		opacity: 0.8,
	},
	headerContainer: {
		alignItems: 'flex-start',
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.md,
	},
	modeContainer: {
		flexDirection: 'row',
		gap: space.xs,
	},
	modeButton: {
		width: touch.min,
		height: touch.min,
		borderRadius: radius.control,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.card,
		alignItems: 'center',
		justifyContent: 'center',
	},
	modeButtonSelected: {
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	optionsContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		paddingHorizontal: space.gutter,
		paddingBottom: space.md,
		borderBottomWidth: 1,
		borderBottomColor: colors.border,
	},
	callouts: {
		paddingHorizontal: space.gutter,
		paddingTop: space.md,
	},
});

export default DetailContainer(PickListSummary);
